/**
 * @fileoverview Payments service using axios client and Zod validation.
 * @module features/payments/api/paymentsService
 */

import apiClient from '@/lib/apiClient'
import { pageSchema, type PaymentsPageResponse } from '../schemas/paymentsSchema'

/**
 * Query parameters for listing payments (universal transactions view).
 */
export interface ListPaymentsParams {
  startDate?: string // YYYY-MM-DD inclusive
  endDate?: string // YYYY-MM-DD exclusive
  page?: number
  size?: number
  sort?: string | string[]
  direction?: 'debit' | 'credit'
  search?: string
}

/**
 * List payments filtered by date and direction=debit.
 * Validates response with Zod and fails fast on errors.
 *
 * @param params Query params (dates, paging)
 * @returns Parsed PaymentsPageResponse
 */
export async function listPayments(params: ListPaymentsParams): Promise<PaymentsPageResponse> {
  const queryParams: Record<string, unknown> = {
    startDate: params.startDate,
    endDate: params.endDate,
    page: params.page ?? 0,
    size: params.size ?? 50,
    ...(params.search ? { search: params.search } : {}),
  }

  if (params.sort) {
    // Handle "field,direction" format by splitting
    if (typeof params.sort === 'string' && params.sort.includes(',')) {
      const [field, dir] = params.sort.split(',')
      queryParams.sort = field
      queryParams.directionParam = dir
    } else {
      queryParams.sort = params.sort
    }
  }

  // Only include direction when explicitly provided. When omitted, backend returns all directions.
  if (params.direction) {
    queryParams.direction = params.direction.toLowerCase()
  }

  const response = await apiClient.get('/universal-transactions', {
    params: queryParams,
  })
  const parsed = pageSchema.safeParse(response.data)
  if (!parsed.success) {
    throw new Error('API validation failed for /universal-transactions')
  }
  return parsed.data
}

export default { listPayments }
